import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useClub } from '../contexts/ClubContext';

/**
 * Page shown when club subscription is suspended or cancelled
 */
function SubscriptionBlocked({ status = 'suspended' }) {
  const { club } = useClub();
  const [user] = useAuthState(auth);
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchParams] = useSearchParams();
  const clubParam = searchParams.get('club');

  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const role = userDoc.data().role;
          setIsAdmin(role === 'club_admin' || role === 'super_admin');
        }
      } catch (err) {
        console.error('Error checking admin status:', err);
      }
    };

    checkAdmin();
  }, [user]);

  const getMessage = () => {
    if (status === 'cancelled') {
      return {
        title: 'Subscription Cancelled',
        message: 'This club\'s subscription has been cancelled.',
        icon: '🚫'
      };
    }
    if (status === 'past_due') {
      return {
        title: 'Payment Required',
        message: 'This club\'s subscription payment is overdue. Access is temporarily limited.',
        icon: '⏰'
      };
    }
    return {
      title: 'Subscription Expired',
      message: 'This club\'s subscription has expired due to non-payment.',
      icon: '⚠️'
    };
  };

  const { title, message, icon } = getMessage();

  return (
    <div className="subscription-blocked-page">
      <div className="blocked-content">
        <div className="blocked-icon">{icon}</div>
        <h1>{title}</h1>
        <p className="blocked-message">{message}</p>
        
        {club && (
          <div className="blocked-club-info">
            <p><strong>Club:</strong> {club.name}</p>
          </div>
        )}

        {isAdmin ? (
          <div className="blocked-admin-section">
            <p>As the club administrator, you can renew the subscription to restore access.</p>
            <Link 
              to={`/admin/subscription${clubParam ? `?club=${clubParam}` : ''}`}
              className="btn-renew"
            >
              Renew Subscription - TT$ 500
            </Link>
          </div>
        ) : (
          <div className="blocked-user-section">
            <p>Please contact your club administrator to restore access.</p>
            {club?.email && (
              <p className="contact-email">
                Club Email: <a href={`mailto:${club.email}`}>{club.email}</a>
              </p>
            )}
          </div>
        )}

        <div className="blocked-footer">
          <div className="footer-brand">
            <span>ClubLink TT</span>
          </div>
          <p>Football Club Management Platform</p>
          <p className="support-text">
            Need help? Contact support at <a href="mailto:support@clublinktt.com">support@clublinktt.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionBlocked;
